import { API } from '..';
import { requireTotpParams } from '../../utils/admin-stepup';

// Cab Ads Manager — Tài khoản quảng cáo DOANH NGHIỆP (E1: KYB).
export interface AdAccount {
  id: string;
  ownerUserId: string;
  /** Business (mặc định, mọi tài khoản cũ) | Individual (cá nhân đã eKYC, đặt đơn sàn creator bằng Ca nạp). */
  kind?: 'Business' | 'Individual';
  businessName: string;
  taxCode: string;
  businessLicenseNo?: string | null;
  address?: string | null;
  representativeName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  licenseImageUrl?: string | null;
  taxImageUrl?: string | null;
  authorizationImageUrl?: string | null;
  status: string; // Pending | Verified | Rejected
  rejectReason?: string | null;
  balanceVnd?: number; // số dư ví DN (đồng) — dùng khi chọn nguồn ngân sách chạy quảng cáo (E3). Âm = công nợ (E5)
  creditLimitVnd?: number; // hạn mức tín dụng/trả sau admin cấp (E5); 0 = không có
  taxVerifiedName?: string | null; // tên DN theo cơ quan thuế (tra từ MST)
  taxVerifiedAddress?: string | null;
  taxMatch?: boolean | null; // tên khai có khớp tên theo thuế không; null = chưa tra được
  ownerIdentityVerified?: boolean; // người đại diện đã eKYC khi mở tài khoản
  ownerIdentityName?: string | null; // tên người đại diện theo eKYC (CCCD)
  createdAt: string;
  verifiedAt?: string | null;
  /** M4: tài khoản bị khoá khỏi sàn hợp tác creator (admin xác nhận kháng cáo bản quyền vi phạm, hoặc
   * SuperAdmin khoá tay) — chặn tạo/sửa chiến dịch/brief, mời trực tiếp, mở sàn/đăng brief, duyệt đơn. */
  marketplaceBanned?: boolean;
  marketplaceBanReason?: string | null;
  /** Hàng chờ KYB: mã số thuế hoặc CCCD người đại diện trùng danh sách đen (spec 2026-10-09 xử phạt §4.5). */
  blacklistHit?: boolean;
}

// Kết quả tra cứu MST (đối chiếu với cơ quan thuế qua VietQR).
export interface TaxLookupResult {
  found: boolean; // tra được DN theo MST
  name?: string | null;
  address?: string | null;
  lookupEnabled: boolean; // false = admin chưa cấu hình key → duyệt tay
}

export const lookupTaxCode = (taxCode: string) =>
  API.get<TaxLookupResult>(
    `/v1/payment-service/ad-accounts/tax-lookup?taxCode=${encodeURIComponent(taxCode)}`
  ).then((r) => r.data);

export interface CreateAdAccountPayload {
  businessName: string;
  taxCode: string;
  businessLicenseNo?: string;
  address?: string;
  representativeName?: string;
  contactEmail?: string;
  contactPhone?: string;
  licenseImageUrl?: string;
  taxImageUrl?: string;
  authorizationImageUrl?: string;
}

export const createAdAccount = (payload: CreateAdAccountPayload) =>
  API.post<AdAccount>('/v1/payment-service/ad-accounts', payload).then((r) => r.data);

export const getMyAdAccounts = () =>
  API.get<AdAccount[]>('/v1/payment-service/ad-accounts/mine').then((r) => r.data);

// Mở "tài khoản đặt đơn loại Cá nhân" (cần đã eKYC). Gọi lại khi đã có thì BE trả tài khoản cũ.
// Chưa eKYC: 400 { message, code: 'EKYC_REQUIRED' }.
export const createIndividualAdAccount = () =>
  API.post<AdAccount>('/v1/payment-service/ad-accounts/individual').then((r) => r.data);

// ── Ví doanh nghiệp (E2) ──
export interface TopUp {
  id: string;
  adAccountId: string;
  businessName: string;
  amountVnd: number;
  transferNote: string;
  payerAccountName?: string | null; // tên chủ TK chuyển tiền (chống mạo danh)
  payerAccountNo?: string | null;
  payerBankName?: string | null;
  payerNameMatch?: boolean | null; // có khớp tên công ty không
  status: string; // Pending | Confirmed | Rejected
  adminNote?: string | null;
  createdAt: string;
  confirmedAt?: string | null;
}
export interface BusinessWallet {
  adAccountId: string;
  balanceVnd: number;
  creditLimitVnd: number; // hạn mức tín dụng (E5)
  outstandingDebtVnd: number; // công nợ hiện tại = max(0, -balanceVnd)
  availableVnd: number; // còn được tiêu = balanceVnd + creditLimitVnd
  topUps: TopUp[];
}
export interface RequestTopUpResult {
  topUpId: string;
  amountVnd: number;
  transferNote: string;
  bankName: string;
  bankAccountNo: string;
  bankAccountName: string;
  payerNameMatch: boolean; // tên chủ TK khai có khớp tên công ty không
}
export interface RequestTopUpPayload {
  amountVnd: number;
  payerAccountName: string; // bắt buộc — nên đứng tên công ty
  payerAccountNo?: string;
  payerBankName?: string;
}

export const getBusinessWallet = (accountId: string) =>
  API.get<BusinessWallet>(`/v1/payment-service/ad-accounts/${accountId}/wallet`).then((r) => r.data);

export const requestTopUp = (accountId: string, payload: RequestTopUpPayload) =>
  API.post<RequestTopUpResult>(`/v1/payment-service/ad-accounts/${accountId}/topup`, payload).then(
    (r) => r.data
  );

export const adminListPendingTopUps = () =>
  API.get<TopUp[]>('/v1/payment-service/admin/ads/topups').then((r) => r.data);

// Máy chủ bắt mã Authenticator (thao tác cộng tiền ví DN) — hỏi mã ngay tại đây.
export const adminReviewTopUp = async (
  topUpId: string,
  confirm: boolean,
  adminNote?: string,
  forceConfirmNameMismatch?: boolean
) => {
  const params = requireTotpParams(confirm ? 'xác nhận nạp ví doanh nghiệp' : 'từ chối yêu cầu nạp');
  return API.post<TopUp>(
    `/v1/payment-service/admin/ads/topups/${topUpId}/review`,
    { confirm, adminNote, forceConfirmNameMismatch },
    { params }
  ).then((r) => r.data);
};

// Admin KYB
export const adminListBusinessAccounts = (status = 'Pending') =>
  API.get<AdAccount[]>(
    `/v1/payment-service/admin/ads/business-accounts?status=${status}`
  ).then((r) => r.data);

export const adminReviewBusinessAccount = async (
  accountId: string,
  approve: boolean,
  rejectReason?: string
) => {
  const params = requireTotpParams(approve ? 'duyệt hồ sơ doanh nghiệp' : 'từ chối hồ sơ doanh nghiệp');
  return API.post<AdAccount>(
    `/v1/payment-service/admin/ads/business-accounts/${accountId}/review`,
    { approve, rejectReason },
    { params }
  ).then((r) => r.data);
};

// Admin cấp/điều chỉnh hạn mức tín dụng (trả sau) cho tài khoản DN (E5).
export const adminSetAdAccountCredit = async (accountId: string, creditLimitVnd: number) => {
  const params = requireTotpParams('đổi hạn mức trả sau');
  return API.post<AdAccount>(
    `/v1/payment-service/admin/ads/business-accounts/${accountId}/credit`,
    { creditLimitVnd },
    { params }
  ).then((r) => r.data);
};
